import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { format, parseISO, startOfDay, endOfDay, isSameDay, isWithinInterval } from "date-fns";
import { ptBR } from "date-fns/locale";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { 
  X, 
  CalendarIcon, 
  Image as ImageIcon, 
  Loader2, 
  AlertCircle,
  Filter,
  FilterX
} from "lucide-react";

// Interface para foto com timestamp preciso
interface Photo {
  id: string;
  src: string;
  alt: string;
  timestamp: Date;
  workId?: string;
}

// Interface para agrupamento por dia
interface PhotoGroup {
  date: string;
  displayDate: string;
  photos: Photo[];
}

// Fotos reais: payload dos RDOs da obra (ou de todas as obras do usuário)
async function loadRealPhotos(obraId?: string): Promise<Photo[]> {
  const { listRDOs, getRDOs } = await import("@/services/rdoStorageSupabase");
  const rdos = obraId ? await listRDOs(obraId) : await getRDOs();
  const photos: Photo[] = [];
  for (const rdo of rdos) {
    // Meio-dia local evita off-by-one de timezone no agrupamento por dia
    const rdoDate = new Date(`${rdo.date}T12:00:00`);
    (rdo.photos ?? []).forEach((p, i) => {
      photos.push({
        // Key sempre prefixada pelo RDO: ids do payload podem repetir entre RDOs
        id: `${rdo.id}-${p.id ?? i}`,
        src: p.thumbnailUrl || p.url || "",
        alt: p.caption || `Foto do RDO ${rdo.date}`,
        timestamp: p.timestamp ? new Date(p.timestamp) : rdoDate,
        workId: rdo.obraId,
      });
    });
  }
  return photos.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
}

interface PhotoGalleryModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Quando informado, mostra só as fotos dos RDOs desta obra */
  obraId?: string;
}

export function PhotoGalleryModal({ open, onOpenChange, obraId }: PhotoGalleryModalProps) {
  // Force rebuild to clear selectedDate cache issue
  // States
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadedDays, setLoadedDays] = useState(0);
  const [dateRange, setDateRange] = useState<{ from?: Date; to?: Date }>({});
  const [calendarOpen, setCalendarOpen] = useState(false);

  // Refs
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement>(null);

  // Load photos function — fotos reais dos RDOs (carrega tudo de uma vez)
  const loadPhotos = useCallback(async (_append = false) => {
    if (loading) return;

    setLoading(true);
    setError(null);

    try {
      const newPhotos = await loadRealPhotos(obraId);
      setPhotos(newPhotos);
      setHasMore(false);

    } catch (err) {
      setError("Erro ao carregar fotos. Tente novamente.");
    } finally {
      setLoading(false);
    }
  }, [loading, obraId]);

  // Initial load
  useEffect(() => {
    if (open && photos.length === 0) {
      setLoadedDays(0);
      loadPhotos(false);
    }
  }, [open, photos.length, loadPhotos]);

  // Infinite scroll observer
  useEffect(() => {
    if (!hasMore || loading || dateRange.from || dateRange.to) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadPhotos(true);
        }
      },
      { threshold: 0.1 }
    );

    const trigger = loadMoreTriggerRef.current;
    if (trigger) {
      observer.observe(trigger);
    }

    return () => {
      if (trigger) {
        observer.unobserve(trigger);
      }
    };
  }, [hasMore, loading, dateRange, loadPhotos]);

  // Filter photos by selected date range
  const filteredPhotos = useMemo(() => {
    if (!dateRange.from && !dateRange.to) return photos;
    
    return photos.filter(photo => {
      if (dateRange.from && dateRange.to) {
        // Range filter
        if (isSameDay(dateRange.from, dateRange.to)) {
          // Same day selected
          return isSameDay(photo.timestamp, dateRange.from);
        }
        // Date range
        return isWithinInterval(photo.timestamp, {
          start: startOfDay(dateRange.from),
          end: endOfDay(dateRange.to)
        });
      } else if (dateRange.from) {
        // Only start date selected - show all from that date forward
        return photo.timestamp >= startOfDay(dateRange.from);
      }
      return true;
    });
  }, [photos, dateRange]);

  // Group photos by day
  const photoGroups = useMemo(() => {
    const groups: PhotoGroup[] = [];
    const groupMap = new Map<string, Photo[]>();

    filteredPhotos.forEach(photo => {
      const dateKey = format(photo.timestamp, "yyyy-MM-dd");
      if (!groupMap.has(dateKey)) {
        groupMap.set(dateKey, []);
      }
      groupMap.get(dateKey)!.push(photo);
    });

    // Convert to array and sort by date (newest first)
    Array.from(groupMap.entries())
      .sort(([a], [b]) => new Date(b).getTime() - new Date(a).getTime())
      .forEach(([dateKey, dayPhotos]) => {
        groups.push({
          date: dateKey,
          displayDate: format(parseISO(dateKey), "EEEE, dd 'de' MMMM 'de' yyyy", { locale: ptBR }),
          photos: dayPhotos.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
        });
      });

    return groups;
  }, [filteredPhotos]);

  // Get date range display text
  const getDateRangeText = () => {
    if (!dateRange.from && !dateRange.to) return "";
    
    if (dateRange.from && dateRange.to) {
      if (isSameDay(dateRange.from, dateRange.to)) {
        return format(dateRange.from, "dd/MM/yyyy", { locale: ptBR });
      }
      return `${format(dateRange.from, "dd/MM/yyyy", { locale: ptBR })} - ${format(dateRange.to, "dd/MM/yyyy", { locale: ptBR })}`;
    }
    
    if (dateRange.from) {
      return `A partir de ${format(dateRange.from, "dd/MM/yyyy", { locale: ptBR })}`;
    }
    
    return "";
  };

  const hasDateFilter = dateRange.from || dateRange.to;

  // Handle date selection - first click sets start date, second sets end date
  const handleDateSelect = (date: Date | undefined) => {
    if (!date) return;
    
    if (!dateRange.from) {
      // First click - set start date
      setDateRange({ from: date });
    } else if (!dateRange.to) {
      // Second click - set end date
      if (isSameDay(date, dateRange.from)) {
        // Same day clicked - filter only that day
        setDateRange({ from: date, to: date });
      } else {
        // Different day - set range (ensure proper order)
        const from = date < dateRange.from ? date : dateRange.from;
        const to = date > dateRange.from ? date : dateRange.from;
        setDateRange({ from, to });
      }
      setCalendarOpen(false);
    } else {
      // Third click - reset and start over
      setDateRange({ from: date });
    }
  };

  const clearFilter = () => {
    setDateRange({});
  };

  // Handle modal open/close
  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      // Reset state when closing
      setPhotos([]);
      setLoadedDays(0);
      setHasMore(true);
      setDateRange({});
      setError(null);
    }
    onOpenChange(newOpen);
  };

  // Keyboard handler
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && open) {
        handleOpenChange(false);
      }
    };

    if (open) {
      document.addEventListener("keydown", handleKeyDown);
      // Prevent background scrolling
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "unset";
    };
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent 
        className="max-w-4xl h-[90vh] flex flex-col"
        aria-labelledby="photo-gallery-title"
        aria-describedby="photo-gallery-description"
      >
        <DialogHeader className="flex-shrink-0">
          <div className="flex items-center justify-between">
            <DialogTitle id="photo-gallery-title" className="text-xl font-semibold">
              Galeria de Fotos
            </DialogTitle>
            <div className="flex items-center gap-1.5 mr-4">
              {/* Date Filter */}
              <Popover open={calendarOpen} onOpenChange={setCalendarOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    aria-label="Filtrar por data"
                  >
                    <CalendarIcon className="h-3.5 w-3.5" />
                    Filtrar por data
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="end">
                  <Calendar
                    mode="single"
                    selected={dateRange.from}
                    onSelect={handleDateSelect}
                    initialFocus
                    className={cn("p-3 pointer-events-auto")}
                    locale={ptBR}
                    disabled={(date) => date > new Date()}
                  />
                  <div className="p-3 border-t text-xs text-muted-foreground">
                    {!dateRange.from 
                      ? "Clique para selecionar data inicial" 
                      : !dateRange.to 
                        ? "Clique para selecionar data final"
                        : "Clique para reiniciar seleção"
                    }
                  </div>
                </PopoverContent>
              </Popover>

              <Button
                variant="ghost"
                size="sm"
                onClick={() => handleOpenChange(false)}
                aria-label="Fechar galeria"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Active Filter Badge */}
          {hasDateFilter && (
            <div className="flex items-center gap-1.5 mt-2">
              <Badge variant="secondary" className="gap-1.5">
                <Filter className="h-3 w-3" />
                {getDateRangeText()}
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                onClick={clearFilter}
                className="h-6 px-2 text-xs"
              >
                <FilterX className="h-3 w-3 mr-0.5" />
                Limpar filtro
              </Button>
            </div>
          )}

          <p id="photo-gallery-description" className="text-sm text-muted-foreground">
            {hasDateFilter 
              ? `Mostrando fotos de ${getDateRangeText()}`
              : "Fotos organizadas por dia, do mais recente para o mais antigo"
            }
          </p>
        </DialogHeader>

        {/* Content Area */}
        <div className="flex-1 min-h-0">
          <ScrollArea className="h-full" ref={scrollAreaRef}>
            <div className="px-4 pb-4 pt-2 space-y-3">
              {/* Error State */}
              {error && (
                <div className="flex items-center justify-center py-8">
                  <div className="text-center">
                    <AlertCircle className="h-8 w-8 text-destructive mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground mb-4">{error}</p>
                    <Button onClick={() => loadPhotos(false)} variant="outline" size="sm">
                      Tentar novamente
                    </Button>
                  </div>
                </div>
              )}

              {/* Photo Groups */}
              {photoGroups.map((group) => (
                <div key={group.date} className="space-y-2">
                  {/* Day Header */}
                  <div className="sticky top-0 bg-background/95 backdrop-blur-sm py-2 border-b">
                    <h3 className="text-sm font-semibold text-foreground capitalize">
                      {group.displayDate}
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      {group.photos.length} foto{group.photos.length !== 1 ? 's' : ''}
                    </p>
                  </div>

                  {/* Photos Grid */}
                  {group.photos.length > 0 ? (
                    <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                      {group.photos.map((photo) => (
                        <div
                          key={photo.id}
                          className="group relative aspect-square bg-muted rounded-lg overflow-hidden cursor-pointer hover:shadow-md transition-shadow"
                        >
                          {photo.src ? (
                            <img
                              src={photo.src}
                              alt={photo.alt}
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-full h-full bg-muted flex items-center justify-center">
                              <ImageIcon className="w-8 h-8 text-muted-foreground" />
                            </div>
                          )}
                          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors" />
                          <div className="absolute bottom-0 left-0 right-0 p-1.5 bg-gradient-to-t from-black/80 to-transparent">
                            <p className="text-xs text-white font-medium truncate">
                              {photo.alt}
                            </p>
                            <p className="text-[10px] text-white/80">
                              {format(photo.timestamp, "HH:mm")}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-muted-foreground">
                      <ImageIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                      <p className="text-sm">Sem fotos neste dia</p>
                    </div>
                  )}
                </div>
              ))}

              {/* Loading State */}
              {loading && (
                <div className="space-y-6">
                  {Array.from({ length: 2 }).map((_, i) => (
                    <div key={i} className="space-y-3">
                      <Skeleton className="h-6 w-48" />
                      <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3">
                        {Array.from({ length: 6 }).map((_, j) => (
                          <Skeleton key={j} className="aspect-square" />
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Load More Trigger */}
              {!hasDateFilter && hasMore && !loading && !error && (
                <div ref={loadMoreTriggerRef} className="flex justify-center py-4">
                  <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                </div>
              )}

              {/* End of List */}
              {!hasDateFilter && !hasMore && !loading && photoGroups.length > 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <p className="text-sm">Todas as fotos foram carregadas</p>
                </div>
              )}

              {/* Empty State */}
              {photoGroups.length === 0 && !loading && !error && (
                <div className="text-center py-12">
                  <ImageIcon className="h-16 w-16 mx-auto mb-4 opacity-50 text-muted-foreground" />
                  <p className="text-lg font-medium text-foreground mb-2">
                    {hasDateFilter ? "Nenhuma foto encontrada" : "Nenhuma foto disponível"}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {hasDateFilter 
                      ? `Não há fotos para ${getDateRangeText()}`
                      : "Comece adicionando algumas fotos ao projeto"
                    }
                  </p>
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
}